import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useUIStore } from '@/lib/ui-store';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Wallet,
  FileText,
  TrendingUp,
  Settings,
  LogOut,
  PieChart,
  Activity,
  BrainCircuit,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  LayoutDashboard,
  Plus,
  Search,
  AlertCircle,
  Calculator,
  LineChart,
  Coins,
  Receipt,
  Briefcase,
  Target,
  Users,
  X,
  ExternalLink,
  Bell,
  Globe,
  Zap,
  Binary,
  Database,
  Server,
  RefreshCcw,
  Scale,
  History
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSession, logout, Session } from '@/lib/store';

/**
 * Sidebar Component (Client Side)
 * Rebuilt for professional Fintech UI
 */
export function Sidebar({ shrink = false }: { shrink?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { isSidebarOpen, closeSidebar, isSidebarCollapsed, toggleSidebarCollapse } = useUIStore();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const isAdminContext = pathname?.startsWith('/dashboard/admin');
  const isLegalContext = pathname?.startsWith('/dashboard/companies');
  const isFinanceContext = pathname?.startsWith('/dashboard/finance');
  const isGenvestContext = pathname?.startsWith('/dashboard/genvest');
  const isInfraContext = pathname?.startsWith('/dashboard/infrastructure');

  const currentSystem = isAdminContext ? 'ADMIN' : (isLegalContext ? 'LEGAL' : (isFinanceContext ? 'FINANCE' : (isGenvestContext ? 'INVESTMENT' : (isInfraContext ? 'INFRA' : 'GLOBAL'))));

  const systems = [
    { id: 'GLOBAL', name: t('dashboard.nav.dashboard'), icon: LayoutDashboard, href: '/dashboard' },
    { id: 'LEGAL', name: t('dashboard.nav.legal'), icon: ShieldCheck, href: '/dashboard/companies' },
    { id: 'FINANCE', name: t('dashboard.nav.finance'), icon: Wallet, href: '/dashboard/finance' },
    { id: 'INVESTMENT', name: t('dashboard.nav.genvest'), icon: PieChart, href: '/dashboard/genvest' },
    { id: 'INFRA', name: t('dashboard.nav.infrastructure'), icon: Activity, href: '/dashboard/infrastructure' },
    { id: 'ADMIN', name: 'Admin Panel', icon: ShieldCheck, href: '/dashboard/admin' },
  ];

  const mainNavigation = [
    { id: 'overview', name: t('dashboard.nav.home'), icon: LayoutDashboard, href: '/dashboard', desc: t('dashboard.desc.dashboard') },
    { id: 'reports', name: t('dashboard.nav.my_documents'), icon: FileText, href: '/dashboard/reports', desc: t('dashboard.desc.my_documents') },
    { id: 'entities', name: t('dashboard.nav.my_companies'), icon: Building2, href: '/dashboard/companies', desc: t('dashboard.desc.my_companies') },
    { id: 'finances', name: t('dashboard.nav.my_treasury'), icon: Wallet, href: '/dashboard/finance', desc: t('dashboard.desc.my_treasury') },
    { id: 'invest', name: t('dashboard.nav.my_assets'), icon: Activity, href: '/dashboard/genvest', desc: t('dashboard.desc.my_assets') },
    { id: 'infra', name: t('dashboard.nav.infrastructure'), icon: Zap, href: '/dashboard/infrastructure', desc: t('dashboard.desc.infra_core') },
    { id: 'settings', name: t('dashboard.nav.settings'), icon: Settings, href: '/dashboard/settings', desc: t('dashboard.desc.settings') },
  ];

  const legalNavigation = [
    { id: 'legal-overview', name: t('dashboard.nav.legal_overview'), icon: LayoutDashboard, href: '/dashboard/companies', desc: t('dashboard.desc.legal_overview') },
    { id: 'legal-name-check', name: t('dashboard.nav.trademark_validation'), icon: Search, href: '/dashboard/companies/validation-marque', desc: t('dashboard.desc.trademark_validation') },
    { id: 'legal-ai', name: t('dashboard.nav.ai_assistant'), icon: BrainCircuit, href: '/dashboard/companies/ai-legal', desc: t('dashboard.desc.ai_assistant') },
    { id: 'legal-simulation', name: t('dashboard.nav.legal_simulation'), icon: Scale, href: '/dashboard/companies/simulator', desc: t('dashboard.desc.legal_simulation') },
    { id: 'legal-creation', name: t('dashboard.nav.create_company'), icon: Plus, href: '/dashboard/companies/creer-entreprise', desc: t('dashboard.desc.create_company') },
    { id: 'legal-events', name: t('dashboard.nav.legal_events'), icon: History, href: '/dashboard/companies/gestion', desc: t('dashboard.desc.legal_events') },
    { id: 'legal-documents', name: t('dashboard.nav.documents'), icon: FileText, href: '/dashboard/companies/documents', desc: t('dashboard.desc.documents') },
    { id: 'legal-partners', name: t('dashboard.nav.our_partners'), icon: Users, href: '/dashboard/companies/partners', desc: t('dashboard.desc.our_partners') },
    { id: 'legal-integrations', name: t('dashboard.nav.integrations'), icon: Zap, href: '/dashboard/companies/integrations', desc: t('dashboard.desc.integrations') },
    { id: 'legal-conformity', name: t('dashboard.nav.compliance'), icon: AlertCircle, href: '/dashboard/companies/conformite', desc: t('dashboard.desc.compliance') },
  ];

  const financeNavigation = [
    { id: 'finance-overview', name: t('dashboard.nav.finance_home'), icon: LayoutDashboard, href: '/dashboard/finance', desc: t('dashboard.desc.finance_home') },
    { id: 'alertes', name: t('dashboard.nav.strategic_alerts'), icon: Bell, href: '/dashboard/finance/alertes', desc: t('dashboard.desc.strategic_alerts') },
    { id: 'comptabilite', name: t('dashboard.nav.financial_management'), icon: Calculator, href: '/dashboard/finance/comptabilite', desc: t('dashboard.desc.financial_management') },
    { id: 'advisor', name: t('dashboard.nav.advisor_insight'), icon: BrainCircuit, href: '/dashboard/finance/advisor', desc: t('dashboard.desc.advisor_insight') },
    { id: 'caisse', name: t('dashboard.nav.treasury'), icon: Coins, href: '/dashboard/finance/tresorerie', desc: t('dashboard.desc.treasury') },
    { id: 'evaluation', name: t('dashboard.nav.project_evaluation'), icon: Target, href: '/dashboard/finance/evaluation', desc: t('dashboard.desc.project_evaluation') },
    { id: 'projets', name: t('dashboard.nav.project_financial_study'), icon: TrendingUp, href: '/dashboard/finance/projets', desc: t('dashboard.desc.project_financial_study') },
    { id: 'e-taxes', name: t('dashboard.nav.e_taxes'), icon: Receipt, href: '/dashboard/finance/e-taxes', desc: t('dashboard.desc.e_taxes') },
    { id: 'reports', name: t('dashboard.nav.financial_reports'), icon: LineChart, href: '/dashboard/finance/reports', desc: t('dashboard.desc.financial_reports') },
    { id: 'indicateurs', name: t('dashboard.nav.performance_indicators'), icon: LineChart, href: '/dashboard/finance/indicateurs', desc: t('dashboard.desc.performance_indicators') },
    { id: 'finance-partners', name: t('dashboard.nav.our_partners'), icon: Users, href: '/dashboard/finance/partenaires', desc: t('dashboard.desc.institutional_partners') },
  ];

  const genvestNavigation = [
    { id: 'genvest-accueil', name: t('dashboard.nav.home'), icon: LayoutDashboard, href: '/dashboard/genvest', desc: t('dashboard.desc.dashboard') },
    { id: 'genvest-simulation', name: t('dashboard.nav.portfolio_simulation'), icon: BrainCircuit, href: '/dashboard/genvest/portfolio-simulation', desc: t('dashboard.desc.portfolio_simulation') },
    { id: 'genvest-creation', name: t('dashboard.nav.creation'), icon: Plus, href: '/dashboard/genvest/creation', desc: t('dashboard.desc.creation_portfolio') },
    { id: 'genvest-portefeuille', name: t('dashboard.nav.portfolio'), icon: Briefcase, href: '/dashboard/genvest/portefeuille', desc: t('dashboard.desc.portfolio_follow') },
    { id: 'risques', name: t('dashboard.nav.risks'), icon: ShieldCheck, href: '/dashboard/genvest/risques', desc: t('dashboard.desc.exposure_analysis') },
    { id: 'simulation-risques', name: t('dashboard.nav.risk_simulation'), icon: Calculator, href: '/dashboard/genvest/simulation-risques', desc: t('dashboard.desc.risk_scenarios') },
    { id: 'ai-predictive', name: t('dashboard.nav.ai_predictive'), icon: BrainCircuit, href: '/dashboard/genvest/ai-predictive', desc: t('dashboard.desc.strategic_analytics') },
    { id: 'marché', name: t('dashboard.nav.market'), icon: Globe, href: '/dashboard/genvest/opportunites', desc: t('dashboard.desc.live_stream') },
    { id: 'deal-flow', name: t('dashboard.nav.institutional_funding'), icon: Building2, href: '/dashboard/genvest/deal-flow', desc: t('dashboard.desc.sovereign_pipeline') },
    { id: 'genvest-alertes', name: t('dashboard.nav.strategic_alerts'), icon: Bell, href: '/dashboard/genvest/alertes', desc: t('dashboard.desc.strategic_alerts') },
    { id: 'integrations', name: t('dashboard.nav.integrations'), icon: BrainCircuit, href: '/dashboard/genvest/integrations', desc: t('dashboard.desc.strategic_partners') },
  ];

  const infraNavigation = [
    { id: 'infra-overview', name: t('dashboard.nav.infra_cockpit'), icon: Zap, href: '/dashboard/infrastructure', desc: t('dashboard.desc.infra_core') },
    { id: 'db-systems', name: t('dashboard.nav.databases'), icon: Database, href: '/dashboard/infrastructure/databases', desc: t('dashboard.desc.distributed_systems') },
    { id: 'security-systems', name: t('dashboard.nav.security'), icon: ShieldCheck, href: '/dashboard/infrastructure/security', desc: t('dashboard.desc.cybersecurity') },
    { id: 'crm-system', name: t('dashboard.nav.crm_users'), icon: Users, href: '/dashboard/infrastructure/crm', desc: t('dashboard.desc.customer_engine') },
    { id: 'load-balancing', name: t('dashboard.nav.connectivity'), icon: Server, href: '/dashboard/infrastructure/connectivity', desc: t('dashboard.desc.load_balancing') },
    { id: 'backup-recovery', name: t('dashboard.nav.recovery'), icon: RefreshCcw, href: '/dashboard/infrastructure/recovery', desc: t('dashboard.desc.backup_plan') },
  ];
  const adminSections = [
    {
      title: 'Overview',
      items: [
        { id: 'admin-dashboard', name: 'Dashboard', icon: LayoutDashboard, href: '/dashboard/admin?view=dashboard', desc: 'Cockpit overview' },
        { id: 'admin-analytics', name: 'Analytics', icon: TrendingUp, href: '/dashboard/admin?view=analytics', desc: 'KPI Indicators' },
        { id: 'admin-activity', name: 'Activity Center', icon: Activity, href: '/dashboard/admin?view=activity', desc: 'Operational log' },
      ]
    },
    {
      title: 'Users',
      items: [
        { id: 'user-citizens', name: 'Citizens', icon: Users, href: '/dashboard/admin?view=users&role=citizen', desc: 'Citizen registry' },
        { id: 'user-entrepreneurs', name: 'Entrepreneurs', icon: Users, href: '/dashboard/admin?view=users&role=entrepreneur', desc: 'Founders database' },
        { id: 'user-startups', name: 'Startups', icon: Building2, href: '/dashboard/admin?view=users&role=startup', desc: 'Active Startups' },
        { id: 'user-experts', name: 'Experts', icon: Users, href: '/dashboard/admin?view=users&role=expert', desc: 'Expert network' },
        { id: 'user-mentors', name: 'Mentors', icon: Users, href: '/dashboard/admin?view=users&role=mentor', desc: 'Incubator mentors' },
        { id: 'user-admins', name: 'Administrators', icon: ShieldCheck, href: '/dashboard/admin?view=users&role=administrator', desc: 'System operators' },
      ]
    },
    {
      title: 'Organizations',
      items: [
        { id: 'org-companies', name: 'Companies', icon: Building2, href: '/dashboard/admin?view=organizations&type=company', desc: 'Registered businesses' },
        { id: 'org-incubators', name: 'Incubators', icon: ShieldCheck, href: '/dashboard/admin?view=organizations&type=incubator', desc: 'Incubation nodes' },
        { id: 'org-universities', name: 'Universities', icon: Globe, href: '/dashboard/admin?view=organizations&type=university', desc: 'Academic centers' },
        { id: 'org-institutions', name: 'Institutions', icon: ShieldCheck, href: '/dashboard/admin?view=organizations&type=institution', desc: 'State portals' },
      ]
    },
    {
      title: 'Documents',
      items: [
        { id: 'doc-queue', name: 'Validation Queue', icon: FileText, href: '/dashboard/admin?view=documents&status=pending', desc: 'Pending review' },
        { id: 'doc-approved', name: 'Approved Documents', icon: FileText, href: '/dashboard/admin?view=documents&status=approved', desc: 'Signed archive' },
        { id: 'doc-rejected', name: 'Rejected Documents', icon: FileText, href: '/dashboard/admin?view=documents&status=rejected', desc: 'Corrective tasks' },
        { id: 'doc-archived', name: 'Archived Documents', icon: FileText, href: '/dashboard/admin?view=documents&status=archived', desc: 'Vault files' },
      ]
    },
    {
      title: 'Programs',
      items: [
        { id: 'prg-incubation', name: 'Startup Incubation', icon: ShieldCheck, href: '/dashboard/admin?view=programs&type=incubation', desc: 'Incubator cohort' },
        { id: 'prg-student', name: 'Student Projects', icon: Users, href: '/dashboard/admin?view=programs&type=student', desc: 'Youth grants' },
        { id: 'prg-acceleration', name: 'Acceleration Programs', icon: Zap, href: '/dashboard/admin?view=programs&type=acceleration', desc: 'Scaleup acceleration' },
        { id: 'prg-funding', name: 'Funding Programs', icon: TrendingUp, href: '/dashboard/admin?view=programs&type=funding', desc: 'State capital' },
      ]
    },
    {
      title: 'Finance',
      items: [
        { id: 'fin-applications', name: 'Applications', icon: Wallet, href: '/dashboard/admin?view=finance&type=applications', desc: 'Inflow pipeline' },
        { id: 'fin-grants', name: 'Grants', icon: Coins, href: '/dashboard/admin?view=finance&type=grants', desc: 'Sovereign grants' },
        { id: 'fin-investments', name: 'Investments', icon: TrendingUp, href: '/dashboard/admin?view=finance&type=investments', desc: 'Equity ventures' },
        { id: 'fin-requests', name: 'Funding Requests', icon: Receipt, href: '/dashboard/admin?view=finance&type=requests', desc: 'Capital requests' },
      ]
    },
    {
      title: 'Communication',
      items: [
        { id: 'comm-notifications', name: 'Notifications', icon: Bell, href: '/dashboard/admin?view=communication&type=notifications', desc: 'Push service' },
        { id: 'comm-announcements', name: 'Announcements', icon: Globe, href: '/dashboard/admin?view=communication&type=announcements', desc: 'State warnings' },
        { id: 'comm-email', name: 'Email Center', icon: Briefcase, href: '/dashboard/admin?view=communication&type=email', desc: 'Mail queues' },
        { id: 'comm-messaging', name: 'Messaging', icon: Users, href: '/dashboard/admin?view=communication&type=messaging', desc: 'Internal chat' },
      ]
    },
    {
      title: 'AI Services',
      items: [
        { id: 'ai-assistant', name: 'AI Assistant', icon: BrainCircuit, href: '/dashboard/admin?view=ai&type=assistant', desc: 'Legal Copilot' },
        { id: 'ai-analytics', name: 'AI Analytics', icon: BrainCircuit, href: '/dashboard/admin?view=ai&type=analytics', desc: 'Predictive intelligence' },
        { id: 'ai-recommendations', name: 'AI Recommendations', icon: BrainCircuit, href: '/dashboard/admin?view=ai&type=recommendations', desc: 'Matches generator' },
      ]
    },
    {
      title: 'Infrastructure',
      items: [
        { id: 'infra-servers', name: 'Servers', icon: Server, href: '/dashboard/infrastructure?view=servers', desc: 'Active clusters' },
        { id: 'infra-storage', name: 'Storage', icon: Database, href: '/dashboard/infrastructure/recovery', desc: 'Backup vault' },
        { id: 'infra-apis', name: 'APIs', icon: Zap, href: '/dashboard/infrastructure/connectivity', desc: 'Endpoint gateway' },
        { id: 'infra-monitoring', name: 'Monitoring', icon: Activity, href: '/dashboard/infrastructure?view=monitoring', desc: 'Realtime telemetry' },
        { id: 'infra-logs', name: 'Logs', icon: FileText, href: '/dashboard/infrastructure?view=logs', desc: 'Incident stream' },
        { id: 'infra-security', name: 'Security', icon: ShieldCheck, href: '/dashboard/infrastructure/security', desc: 'Cybersecurity command' },
      ]
    },
    {
      title: 'Settings',
      items: [
        { id: 'set-platform', name: 'Platform Settings', icon: Settings, href: '/dashboard/admin?view=settings&type=platform', desc: 'Global configuration' },
        { id: 'set-languages', name: 'Languages', icon: Globe, href: '/dashboard/admin?view=settings&type=languages', desc: 'Locales and strings' },
        { id: 'set-permissions', name: 'Permissions', icon: ShieldCheck, href: '/dashboard/admin?view=settings&type=permissions', desc: 'RBAC parameters' },
        { id: 'set-audit', name: 'Audit Logs', icon: History, href: '/dashboard/admin?view=settings&type=audit', desc: 'Immutable ledger' },
      ]
    }
  ];

  const navSections = isAdminContext || isInfraContext ? adminSections : (isLegalContext ? [
    {
      title: t('dashboard.sections.main_management'),
      items: [
        legalNavigation.find(n => n.id === 'legal-overview')!,
        legalNavigation.find(n => n.id === 'legal-simulation')!,
        legalNavigation.find(n => n.id === 'legal-name-check')!,
        legalNavigation.find(n => n.id === 'legal-creation')!,
        legalNavigation.find(n => n.id === 'legal-documents')!,
      ]
    },
    {
      title: t('dashboard.sections.intelligence_compliance'),
      items: [
        legalNavigation.find(n => n.id === 'legal-events')!,
        legalNavigation.find(n => n.id === 'legal-ai')!,
        legalNavigation.find(n => n.id === 'legal-conformity')!,
      ]
    },
    {
      title: t('dashboard.sections.partners_ecosystem'),
      items: [
        legalNavigation.find(n => n.id === 'legal-partners')!,
        legalNavigation.find(n => n.id === 'legal-integrations')!,
      ]
    }
  ] : (isFinanceContext ? [
    {
      title: t('dashboard.sections.financial_operations'),
      items: [
        financeNavigation.find(n => n.id === 'finance-overview')!,
        financeNavigation.find(n => n.id === 'comptabilite')!,
        financeNavigation.find(n => n.id === 'e-taxes')!,
        financeNavigation.find(n => n.id === 'caisse')!,
        financeNavigation.find(n => n.id === 'reports')!,
      ]
    },
    {
      title: t('dashboard.sections.strategic_intelligence'),
      items: [
        financeNavigation.find(n => n.id === 'alertes')!,
        financeNavigation.find(n => n.id === 'advisor')!,
      ]
    },
    {
      title: t('dashboard.sections.analysis_projects'),
      items: [
        financeNavigation.find(n => n.id === 'evaluation')!,
        financeNavigation.find(n => n.id === 'projets')!,
        financeNavigation.find(n => n.id === 'indicateurs')!,
      ]
    },
    {
      title: t('dashboard.sections.ecosystem'),
      items: [
        financeNavigation.find(n => n.id === 'finance-partners')!,
      ]
    }
  ]: (isGenvestContext ? [
    {
      title: t('dashboard.sections.operations_assets'),
      items: [
        genvestNavigation.find(n => n.id === 'genvest-accueil')!,
        genvestNavigation.find(n => n.id === 'genvest-portefeuille')!,
        genvestNavigation.find(n => n.id === 'genvest-creation')!,
      ]
    },
    {
      title: t('dashboard.sections.strategic_intelligence'),
      items: [
        genvestNavigation.find(n => n.id === 'genvest-alertes')!,
        genvestNavigation.find(n => n.id === 'ai-predictive')!,
      ]
    },
    {
      title: t('dashboard.sections.analysis_simulation'),
      items: [
        genvestNavigation.find(n => n.id === 'genvest-simulation')!,
        genvestNavigation.find(n => n.id === 'simulation-risques')!,
        genvestNavigation.find(n => n.id === 'risques')!,
      ]
    },
    {
      title: t('dashboard.sections.market_ecosystem'),
      items: [
        genvestNavigation.find(n => n.id === 'marché')!,
        genvestNavigation.find(n => n.id === 'deal-flow')!,
        genvestNavigation.find(n => n.id === 'integrations')!,
      ]
    }
  ]
 : [
    {
      title: t('dashboard.sections.navigation'),
      items: mainNavigation
    }
  ])));

  const navigation = navSections.flatMap(s => s.items);

  const isActive = (path: string) => {
    if (!pathname) return false;
    if (path.startsWith('http')) return false;

    const [targetPathname, targetSearch] = path.split('?');
    const isSubPath = pathname.startsWith(`${targetPathname}/`);
    const isExactPath = pathname === targetPathname;

    if (!isExactPath && !isSubPath) {
      return false;
    }

    const currentParams = new URLSearchParams(location.search);

    const getParam = (params: URLSearchParams, key: string, pathPrefix: string) => {
      const val = params.get(key);
      if (val === null && key === 'view') {
        if (pathPrefix.startsWith('/dashboard/admin')) return 'dashboard';
        if (pathPrefix.startsWith('/dashboard/infrastructure')) return 'servers';
      }
      return val;
    };

    if (targetSearch) {
      const targetParams = new URLSearchParams(targetSearch);
      for (const [key, val] of targetParams.entries()) {
        const currentVal = getParam(currentParams, key, pathname);
        if (currentVal !== val) {
          return false;
        }
      }
      return true;
    }

    const splitPages = ['/dashboard/admin', '/dashboard/infrastructure'];
    if (splitPages.includes(targetPathname)) {
      const currentView = getParam(currentParams, 'view', pathname);
      const defaultView = targetPathname.startsWith('/dashboard/admin') ? 'dashboard' : 'servers';
      if (currentView && currentView !== defaultView) {
        return false;
      }
    }

    return true;
  };

  const activeItem = navigation.find(item => isActive(item.href));
  const [user, setUser] = React.useState<Session | null>(null);

  // Collapsible sections state (default all to open initially)
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({
    'Overview': true,
    'Users': true,
    'Organizations': true,
    'Documents': true,
    'Programs': true,
    'Finance': true,
    'Communication': true,
    'AI Services': true,
    'Infrastructure': true,
    'Settings': true,
  });

  const toggleSection = (title: string) => {
    setExpandedSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  React.useEffect(() => {
    setUser(getSession());
  }, []);

  // Auto-expand parent section if it contains the active navigation item
  React.useEffect(() => {
    let updated = false;
    const nextState = { ...expandedSections };
    navSections.forEach(section => {
      if (section.title) {
        const hasActive = section.items.some(item => isActive(item.href));
        if (hasActive && !expandedSections[section.title]) {
          nextState[section.title] = true;
          updated = true;
        }
      }
    });
    if (updated) {
      setExpandedSections(nextState);
    }
  }, [pathname, location.search]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[45] lg:hidden animate-in fade-in duration-300"
          onClick={closeSidebar}
        />
      )}

      <aside className={cn(
        "fixed inset-y-0 flex flex-col z-50 transition-all duration-300 ease-in-out border-border bg-bg-primary shadow-sm min-h-0",
        isRTL ? "right-0 border-l md:static" : "left-0 border-r md:static",
        isSidebarCollapsed
          ? "w-[280px] md:w-[76px]"
          : "w-[280px]",
        isSidebarOpen
          ? "translate-x-0"
          : isRTL ? "translate-x-full md:translate-x-0" : "-translate-x-full md:translate-x-0",
        pathname === "/dashboard" && "lg:hidden"
      )}>
        {/* Header/Logo section */}
        <div className={cn("border-b border-border flex-shrink-0 relative", isSidebarCollapsed ? "p-4 flex flex-col items-center" : "p-6")}>
          <button 
            onClick={closeSidebar} 
            className={cn(
              "lg:hidden absolute top-6 p-2 text-text-secondary hover:text-text-primary rounded-lg hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary",
              isRTL ? "left-6" : "right-6"
            )}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>

          <Link to="/dashboard" className={cn("flex items-center gap-3 group px-2", isSidebarCollapsed ? "justify-center mb-6 mt-2" : "mb-8")}>
            <div className="relative w-8 h-8 p-1.5 flex items-center justify-center border border-border bg-white dark:bg-bg-tertiary rounded-lg shadow-sm group-hover:border-accent-primary/30 transition-all flex-shrink-0">
              <img src="/optimized/charikaty_logo_premium.webp" alt="Charikaty Logo" className="object-contain p-1 w-full h-full" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex flex-col animate-in fade-in duration-200">
                <span className="text-lg font-bold ltr:tracking-tight rtl:tracking-normal text-text-primary">Charikaty</span>
                <span className="text-[10px] text-accent-primary font-semibold ltr:tracking-wider rtl:tracking-normal uppercase">
                  {user ? `${t('common.workspace')} ${user.metadata?.demo ? t('auth.demo.account_name') : user.name}` : t('common.sovereign_os')}
                </span>
              </div>
            )}
          </Link>

          {/* System Switcher */}
          {isSidebarCollapsed ? (
            <div className="relative flex justify-center mt-2 group/switcher">
              <button
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-primary text-white border border-accent-primary shadow-lg shadow-accent-primary/20 hover:scale-105 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
                title={systems.find(s => s.id === currentSystem)?.name}
                aria-label="Switch modules"
              >
                {React.createElement(systems.find(s => s.id === currentSystem)?.icon || LayoutDashboard, { size: 18 })}
              </button>
              {/* Flyout panel on hover */}
              <div className={cn(
                "absolute z-[60] bg-bg-primary border border-border rounded-xl p-2 shadow-xl opacity-0 invisible group-hover/switcher:opacity-100 group-hover/switcher:visible transition-all duration-200 w-40 top-0",
                isRTL ? "right-[110%]" : "left-[110%]"
              )}>
                <p className="text-[8px] font-black uppercase text-text-muted px-2 mb-1.5 ltr:tracking-widest">Modules</p>
                <div className="space-y-1">
                  {systems.map((s) => (
                    <Link
                      key={s.id}
                      to={s.href}
                      className={cn(
                        "flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-xs font-bold transition-all duration-150",
                        currentSystem === s.id
                          ? "bg-accent-primary text-white"
                          : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                      )}
                    >
                      <s.icon size={14} />
                      <span className="truncate">{s.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 px-1">
              {systems.map((s) => (
                <Link
                  key={s.id}
                  to={s.href}
                  className={cn(
                    "flex items-center justify-center aspect-square rounded-lg border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary",
                    currentSystem === s.id
                      ? "bg-accent-primary border-accent-primary text-white shadow-lg shadow-accent-primary/20 scale-[1.05]"
                      : "bg-bg-secondary border-transparent text-text-secondary hover:border-border hover:bg-bg-tertiary hover:text-text-primary"
                  )}
                  title={s.name}
                >
                  <s.icon size={18} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-4 py-4 space-y-0.5 overflow-y-auto custom-scrollbar">
          {navSections.map((section, sIdx) => {
            const isSectionExpanded = !section.title || expandedSections[section.title];
            return (
              <div key={section.title || sIdx} className={cn(sIdx > 0 && "mt-6")}>
                {section.title && (
                  isSidebarCollapsed ? (
                    <div className="h-px bg-border/60 my-4 mx-2" />
                  ) : (
                    <button
                      onClick={() => toggleSection(section.title!)}
                      className="w-full px-4 mb-2 flex items-center justify-between group/header text-left select-none text-[10px] font-bold text-text-muted hover:text-text-primary uppercase ltr:tracking-[0.15em] rtl:tracking-normal transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-primary rounded-md"
                      aria-expanded={isSectionExpanded}
                    >
                      <span className="flex items-center gap-1.5">
                        {section.title}
                        {sIdx === 0 && <span className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-pulse inline-block" />}
                      </span>
                      <ChevronRight
                        size={10}
                        className={cn(
                          "text-text-muted group-hover/header:text-text-primary transition-transform duration-200",
                          isSectionExpanded ? "rotate-90" : "rotate-0"
                        )}
                      />
                    </button>
                  )
                )}
                
                <AnimatePresence initial={false}>
                  {(!section.title || !isSidebarCollapsed || isSectionExpanded) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: isSidebarCollapsed ? "auto" : (isSectionExpanded ? "auto" : 0), opacity: isSidebarCollapsed ? 1 : (isSectionExpanded ? 1 : 0) }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      className="space-y-1 overflow-hidden"
                    >
                      {section.items.map((item) => {
                        const isExternal = item.href.startsWith('http');
                        const active = isActive(item.href);

                        return (
                          <Link
                            key={item.id}
                            to={item.href}
                            onClick={closeSidebar}
                            title={isSidebarCollapsed ? undefined : item.desc}
                            target={isExternal ? "_blank" : undefined}
                            rel={isExternal ? "noopener noreferrer" : undefined}
                            className={cn(
                              "group flex items-center gap-3 py-2 rounded-xl text-sm font-semibold transition-all duration-250 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary relative",
                              isSidebarCollapsed
                                ? "justify-center px-0 h-10"
                                : (section.title ? (isRTL ? "pr-8 pl-4" : "pl-8 pr-4") : "px-4"),
                              active
                                ? "bg-accent-primary/[0.08] dark:bg-accent-primary/[0.12] text-accent-primary dark:text-accent-primary-light font-bold shadow-sm"
                                : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
                            )}
                          >
                            {active && (
                              <span className={cn(
                                "absolute top-1.5 bottom-1.5 w-[3px] bg-accent-primary transition-all duration-200",
                                isRTL ? "right-0 rounded-l" : "left-0 rounded-r"
                              )} />
                            )}
                            <item.icon size={18} className={cn(
                              "transition-colors duration-200 flex-shrink-0",
                              active ? "text-accent-primary dark:text-accent-primary-light" : "text-text-muted group-hover:text-accent-primary"
                            )} />
                            
                            {!isSidebarCollapsed && <span className="truncate animate-in fade-in duration-200">{item.name}</span>}
                            
                            {active && !isSidebarCollapsed && (isRTL
                              ? <ChevronLeft size={14} className="me-auto opacity-70 flex-shrink-0" />
                              : <ChevronRight size={14} className="ms-auto opacity-70 flex-shrink-0" />
                            )}
                            {isExternal && !isSidebarCollapsed && <ExternalLink size={12} className="ml-auto opacity-40 flex-shrink-0" />}

                            {/* CSS Tooltip for Collapsed Sidebar */}
                            {isSidebarCollapsed && (
                              <span className={cn(
                                "absolute z-50 px-2.5 py-1.5 bg-bg-primary border border-border text-text-primary text-xs font-bold rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap pointer-events-none",
                                isRTL ? "right-[110%]" : "left-[110%]"
                              )}>
                                {item.name}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* Footer actions */}
        <div className={cn("border-t border-border bg-bg-secondary/30 flex-shrink-0 space-y-2", isSidebarCollapsed ? "p-2" : "p-4")}>
          {/* Collapse toggle button */}
          <button
            onClick={toggleSidebarCollapse}
            className={cn(
              "w-full flex items-center text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-xl transition-all duration-200 border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary",
              isSidebarCollapsed ? "justify-center h-10 px-0" : "justify-between px-4 py-3 text-sm font-bold"
            )}
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            aria-label={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <span className={cn("flex items-center gap-3", isSidebarCollapsed && "hidden")}>
              {isRTL ? <ChevronRight size={18} className="text-text-muted" /> : <ChevronLeft size={18} className="text-text-muted" />}
              <span>Collapse Menu</span>
            </span>
            {isSidebarCollapsed ? (
              isRTL ? <ChevronLeft size={18} className="text-text-muted" /> : <ChevronRight size={18} className="text-text-muted" />
            ) : null}
          </button>

          <Link
            to="/dashboard/settings"
            className={cn(
              "flex items-center text-text-secondary hover:text-text-primary rounded-xl transition-colors group bg-bg-primary/40 border border-transparent hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary relative",
              isSidebarCollapsed ? "justify-center h-10 px-0" : "gap-3 px-4 py-3 text-sm font-bold"
            )}
            title={isSidebarCollapsed ? t('dashboard.nav.settings') : undefined}
          >
            <Settings size={20} className="text-text-muted group-hover:text-text-primary flex-shrink-0" />
            {!isSidebarCollapsed && <span>{t('dashboard.nav.settings')}</span>}
            {isSidebarCollapsed && (
              <span className={cn(
                "absolute z-50 px-2.5 py-1.5 bg-bg-primary border border-border text-text-primary text-xs font-bold rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap pointer-events-none",
                isRTL ? "right-[110%]" : "left-[110%]"
              )}>
                {t('dashboard.nav.settings')}
              </span>
            )}
          </Link>

          <button
            onClick={handleLogout}
            className={cn(
              "w-full flex items-center text-red-500/70 hover:text-red-500 hover:bg-red-500/5 rounded-xl transition-all border border-transparent hover:border-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary relative",
              isSidebarCollapsed ? "justify-center h-10 px-0" : "gap-3 px-4 py-3 text-sm font-bold"
            )}
            title={isSidebarCollapsed ? t('dashboard.nav.logout') : undefined}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {!isSidebarCollapsed && <span>{t('dashboard.nav.logout')}</span>}
            {isSidebarCollapsed && (
              <span className={cn(
                "absolute z-50 px-2.5 py-1.5 bg-bg-primary border border-border text-red-500 text-xs font-bold rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap pointer-events-none",
                isRTL ? "right-[110%]" : "left-[110%]"
              )}>
                {t('dashboard.nav.logout')}
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
